import { Component, EventEmitter, Input, Output } from '@angular/core';
import { IWorkerConfiguration } from '../interfaces/base-knowledge-catalog';
import { IWorkerConfigurationData } from '../interfaces/worker-configuration-data';
import { I18nService } from 'i18n/i18n.service';
import { WorkerType } from '../enums/worker-type.enum';

@Component({
  selector: 'app-ai-parameters-section',
  templateUrl: './ai-parameters-section.component.html',
  styleUrls: ['./ai-parameters-section.component.scss']
})

export class AiParametersSectionComponent {
  private _workerConfigurationData: IWorkerConfigurationData;

  @Input()
  set workerConfigurationData(value: IWorkerConfigurationData) {
    this._workerConfigurationData = value;
    if (value && value.aIWorkerConfiguration) {
      // Create a deep copy of the worker configuration to avoid mutating the original input data
      this.aIWorkerConfiguration = this.cloneWorkerConfiguration(value.aIWorkerConfiguration);
    } else {
      this.aIWorkerConfiguration = {
        id: '',
        idParent: '',
        type: null,
        enable: false,
        ruleId: '',
        ruleName: ''
      };
    }
  }

  @Input() hasOverride: boolean = false;

  @Output() configurationChange = new EventEmitter<IWorkerConfiguration>();
  @Output() resetAiWorkerConfiguration = new EventEmitter<string>();

  public aIWorkerConfiguration: IWorkerConfiguration;

  // Exponer el enum al template
  public readonly WorkerType = WorkerType;

  constructor(private _i18nService: I18nService) { }

  get workerConfigurationData(): IWorkerConfigurationData | null {
    return this._workerConfigurationData;
  }

  get isWorkerInherited(): boolean {
    return this.workerConfigurationData?.inheritanceInfo?.isInherited ?? false;
  }

  get parentConfigurationName(): string {
    return this.workerConfigurationData?.inheritanceInfo?.parentName ?? '';
  }

  get isWorkerEnabled(): boolean {
    return this.aIWorkerConfiguration?.enable ?? false;
  }

  get workerType(): WorkerType | null {
    return this.aIWorkerConfiguration?.type as WorkerType ?? null;
  }

  get inheritanceTooltip(): string {
    if (!this.isWorkerInherited || !this.parentConfigurationName) {
      return '';
    }

    const inheritedFromText = this._i18nService.instant('AIFORPROCESSES.INHERITED_FROM');
    return `${inheritedFromText}\n${this.parentConfigurationName}`;
  }

  get ruleName(): string {
    return this.aIWorkerConfiguration?.ruleName ?? '';
  }

  onWorkerEnableChange(isEnabled: boolean) {
    if (this.aIWorkerConfiguration) {
      this.ensureLocalCopy();
      this.aIWorkerConfiguration.enable = isEnabled;
      if (isEnabled) {
        if (!this.aIWorkerConfiguration.type)
          this.aIWorkerConfiguration.type = WorkerType.Supervised;
      } else {
        this.aIWorkerConfiguration.type = null;
      }
      this.emitConfigurationChange();
    }
  }

  onWorkerModeChange(mode: WorkerType) {
    if (this.aIWorkerConfiguration) {
      this.ensureLocalCopy();
      this.aIWorkerConfiguration.type = mode;
      this.emitConfigurationChange();
    }
  }

  onResetClick() {
    this.resetAiWorkerConfiguration.emit(this.aIWorkerConfiguration.id);
  }


  private emitConfigurationChange() {
    this.configurationChange.emit(this.aIWorkerConfiguration);
  }

  private cloneWorkerConfiguration(config: IWorkerConfiguration): IWorkerConfiguration {
    return JSON.parse(JSON.stringify(config));
  }

  private ensureLocalCopy() {
    if (this.aIWorkerConfiguration === this.workerConfigurationData?.aIWorkerConfiguration) {
      this.aIWorkerConfiguration = this.cloneWorkerConfiguration(this.aIWorkerConfiguration);
    }
  }
}
